/**
 * Directional Influence (DI) — Quick-Time window for defending players.
 *
 * When a tank is hit with enough knockback, the game enters a 1.5s DI window.
 * The hit player holds a direction to shift their launch angle perpendicular
 * to the knockback vector. Adapted from Smash Bros DI for turn-based flow.
 *
 * Accessibility: any perpendicular input gives 80% of max benefit.
 * "Hold away from the void" works for 90% of situations.
 */

import { Input, Keys } from 'bonkjs';
import { DI, TUMBLE, KNOCKBACK, PHYSICS, LAUNCH_FX } from '../constants';
import { enterTumble } from '../physics/tanks';
import type { GameState, Player } from '../types';
import type { InputProvider } from '../input';
import { mirrorEnterTumble, mirrorAirborne } from '../physics/transitions';

/**
 * Calculate DI angle shift from pending knockback and held input direction.
 * Projects input onto the perpendicular axis of the knockback vector.
 * Returns angle in radians to rotate the knockback.
 */
function calculateDIShift(
    pendingVx: number,
    pendingVy: number,
    inputX: number,
    inputY: number,
): number {
    // Knockback direction
    const kbSpeed = Math.sqrt(pendingVx * pendingVx + pendingVy * pendingVy);
    if (kbSpeed < 0.01) return 0;
    const kbNx = pendingVx / kbSpeed;
    const kbNy = pendingVy / kbSpeed;

    // Perpendicular to knockback (90° CCW)
    const perpX = -kbNy;
    const perpY = kbNx;

    // Normalize input
    const inputLen = Math.sqrt(inputX * inputX + inputY * inputY);
    if (inputLen < 0.01) return 0;
    const normInputX = inputX / inputLen;
    const normInputY = inputY / inputLen;

    // Project input onto perpendicular axis
    const perpDot = normInputX * perpX + normInputY * perpY;

    // Accessibility: any meaningful perpendicular input gives 80% of max shift.
    // Experts who aim precisely get the remaining 20%.
    let rawInfluence: number;
    if (Math.abs(perpDot) > DI.PERPENDICULAR_THRESHOLD) {
        rawInfluence = Math.sign(perpDot) * Math.max(DI.ACCESSIBILITY_FLOOR, Math.abs(perpDot));
    } else {
        rawInfluence = perpDot; // below threshold — linear, less effective
    }

    return rawInfluence * DI.MAX_ANGLE_SHIFT;
}

/**
 * Start DI window for a list of hit players.
 * Queues them and activates the first player's window.
 */
export function startDIWindow(state: GameState, hitPlayerIndices: number[]): boolean {
    // Filter to players above knockback speed threshold
    const diCandidates = hitPlayerIndices.filter(i => {
        const p = state.players[i];
        const speed = Math.sqrt(p.diPendingVx * p.diPendingVx + p.diPendingVy * p.diPendingVy);
        return speed >= DI.MIN_KNOCKBACK_SPEED;
    });

    if (diCandidates.length === 0) {
        // All hits too weak — apply knockback immediately, skip DI
        for (const i of hitPlayerIndices) {
            applyDeferredKnockback(state.players[i]);
        }
        return false;
    }

    // Queue up candidates
    state.diHitQueue = diCandidates;
    activateNextDI(state);
    return true;
}

/**
 * Activate the DI window for the next player in the queue.
 */
function activateNextDI(state: GameState): void {
    if (state.diHitQueue.length === 0) {
        state.diActivePlayer = -1;
        return;
    }

    const playerIndex = state.diHitQueue.shift()!;
    state.diActivePlayer = playerIndex;
    state.diWindowTimer = DI.WINDOW_DURATION;

    // Reset the player's DI angle
    state.players[playerIndex].diAngleShift = 0;
}

/**
 * Read player input and update DI angle shift.
 * Called each fixed update frame during 'di_window' phase.
 * Returns true when the DI window is complete (all players resolved).
 */
export function updateDIWindow(state: GameState, input: InputProvider): boolean {
    if (state.diActivePlayer < 0) return true;

    const player = state.players[state.diActivePlayer];

    // Read directional input (WASD + arrow keys)
    let inputX = 0;
    let inputY = 0;
    if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) inputX -= 1;
    if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) inputX += 1;
    if (input.getKey(Keys.ArrowUp) || input.getKey(Keys.W)) inputY -= 1; // screen Y: up = negative
    if (input.getKey(Keys.ArrowDown) || input.getKey(Keys.S)) inputY += 1;

    // Calculate DI shift from current input
    if (inputX !== 0 || inputY !== 0) {
        player.diAngleShift = calculateDIShift(
            player.diPendingVx,
            player.diPendingVy,
            inputX,
            inputY,
        );
    } else {
        player.diAngleShift = 0; // no input = no DI (neutral)
    }

    // Count down timer
    state.diWindowTimer -= 1 / 60; // fixed timestep

    // Pre-launch anticipation: gradually compress during last 250ms
    // Physics is frozen during DI window, so spring won't fight this
    if (state.diWindowTimer > 0 && state.diWindowTimer < LAUNCH_FX.ANTICIPATION_DURATION) {
        const progress = 1 - (state.diWindowTimer / LAUNCH_FX.ANTICIPATION_DURATION);
        if (player.alive) {
            // Safe: physics (spring integration) is frozen during DI window
            player.squash = 1.0 + (LAUNCH_FX.ANTICIPATION_TARGET - 1.0) * progress;
        }
    }

    // Window expired — resolve this player's DI
    if (state.diWindowTimer <= 0) {
        // Non-DI client waits for authoritative di_resolve from network.
        // This prevents desync from input latency on the final DI frame.
        if (state.diWaitForAuthority) {
            // Safety net: if authority never arrives (disconnect), fall through to local
            if (state.diWindowTimer >= -DI.AUTHORITY_TIMEOUT) {
                return false; // keep waiting for di_resolve
            }
            // Timeout expired — clear flag and resolve locally as fallback
            console.warn('[DI] Authority timeout — resolving locally');
            state.diWaitForAuthority = false;
        }

        resolveDI(state, state.diActivePlayer);

        // Check for more queued players
        if (state.diHitQueue.length > 0) {
            activateNextDI(state);
            return false; // more windows to process
        } else {
            state.diActivePlayer = -1;
            return true; // all done
        }
    }

    return false;
}

/**
 * Resolve DI for a player — rotate pending knockback by diAngleShift, apply velocity.
 */
function resolveDI(state: GameState, playerIndex: number): void {
    const p = state.players[playerIndex];
    const shift = p.diAngleShift;

    // Rotate the pending velocity vector by the DI angle shift
    if (Math.abs(shift) > 0.001) {
        const cos = Math.cos(shift);
        const sin = Math.sin(shift);
        const newVx = p.diPendingVx * cos - p.diPendingVy * sin;
        const newVy = p.diPendingVx * sin + p.diPendingVy * cos;
        p.diPendingVx = newVx;
        p.diPendingVy = newVy;
    }

    // Apply the (possibly rotated) knockback
    applyDeferredKnockback(p);
}

/**
 * Apply deferred knockback velocity to a player and trigger ragdoll if needed.
 * This is the same logic that was in applyBlastKnockback, but applied post-DI.
 */
export function applyDeferredKnockback(p: Player): void {
    p.vx += p.diPendingVx;
    p.vy += p.diPendingVy;

    // Trigger ragdoll if launch speed is high enough
    enterTumble(p, {
        hitstunPerSpeed: TUMBLE.HITSTUN_PER_SPEED,
        minHitstun: TUMBLE.MIN_HITSTUN,
        maxHitstun: TUMBLE.MAX_HITSTUN,
        speedGated: true,
        damagePercent: p.damagePercent,
    });

    if (p.tumble) {
        mirrorEnterTumble(p, 'knockback');
        // Rubber-band snap: compress then spring into stretch
        p.squash = LAUNCH_FX.RESOLVE_SQUASH;
        p.squashVel = LAUNCH_FX.RESOLVE_IMPULSE;

        // Smash-style pop: guarantee minimum upward launch for level/below hits.
        const pendingSpeed = Math.sqrt(p.diPendingVx * p.diPendingVx + p.diPendingVy * p.diPendingVy);
        if (pendingSpeed > 0.01) {
            const ny = p.diPendingVy / pendingSpeed;
            // ny > 0 means hit from below (upward normal), check for level/below hits
            if (ny < 0.5 && p.vy > -KNOCKBACK.BASE_LAUNCH_VY) {
                p.vy = -KNOCKBACK.BASE_LAUNCH_VY;
            }
        }

        // Defensive movement phase will be queued after all DI windows resolve
        // (handled in phases.ts di_window completion)
    }

    // Clear pending knockback
    p.diPendingVx = 0;
    p.diPendingVy = 0;
    p.diAngleShift = 0;
    p.kbStackHitCount = 0;
    p.kbStackFirstFrame = 0;

    // If speed gate rejected tumble, we're no longer di_frozen — transition to airborne
    if (p.actionState.tag === 'di_frozen') {
        mirrorAirborne(p);
    }
}

/**
 * Get the DI-shifted trajectory for rendering preview.
 * Returns the predicted velocity after DI rotation.
 */
export function getDIShiftedVelocity(player: Player): { vx: number; vy: number } {
    const shift = player.diAngleShift;
    if (Math.abs(shift) < 0.001) {
        return { vx: player.diPendingVx, vy: player.diPendingVy };
    }
    const cos = Math.cos(shift);
    const sin = Math.sin(shift);
    return {
        vx: player.diPendingVx * cos - player.diPendingVy * sin,
        vy: player.diPendingVx * sin + player.diPendingVy * cos,
    };
}

/**
 * Get current DI input direction (for rendering the directional arrow).
 */
export function getDIInputDirection(): { x: number; y: number } {
    let x = 0;
    let y = 0;
    if (Input.getKey(Keys.ArrowLeft) || Input.getKey(Keys.A)) x -= 1;
    if (Input.getKey(Keys.ArrowRight) || Input.getKey(Keys.D)) x += 1;
    if (Input.getKey(Keys.ArrowUp) || Input.getKey(Keys.W)) y -= 1;
    if (Input.getKey(Keys.ArrowDown) || Input.getKey(Keys.S)) y += 1;
    // Normalize
    const len = Math.sqrt(x * x + y * y);
    if (len > 0.01) {
        x /= len;
        y /= len;
    }
    return { x, y };
}
